import { colors } from "@/theme";
import styled from "@emotion/styled";
import Icon from "@imminently/decisively-ui/Icon";
import { type ReactNode, useCallback, useRef } from "react";
import { Trans } from "react-i18next";

const DropZone = styled.div`
  border: 2px dashed #ccc;
  border-radius: 1rem;
  padding: 2rem;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 10rem;
  width: 100%;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }

  &.dragging {
    border-color: var(--primary);
    background-color: ${colors.prayingMantis10};
  }
`;

const DropZoneText = styled.p`
  font-size: 1rem;
  color: var(--foreground);
  font-weight: 500;
`;

export interface FileDropzoneProps {
  onLoad?: (fileName: string, fileContent: string, file?: File) => void;
  onFileSelect?: (file: File) => void;
  className?: string;
  accept?: string;
  label?: ReactNode;
}

export const FileDropZone = (props: FileDropzoneProps) => {
  const { onLoad, onFileSelect, className, accept = "application/json,.zip", label } = props;
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelectedFile = useCallback(
    (file: File) => {
      if (onFileSelect) {
        onFileSelect(file);
        return;
      }
      if (onLoad) readFileAsText(file, onLoad);
    },
    [onFileSelect, onLoad],
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        handleSelectedFile(files[0]);
      }
      dropZoneRef.current?.classList.remove("dragging"); // Remove dragging class
    },
    [handleSelectedFile],
  );

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    dropZoneRef.current?.classList.add("dragging"); // Add dragging class
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    dropZoneRef.current?.classList.remove("dragging"); // Remove dragging class
  }, []);

  const handleClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event) => {
      const files = event.target.files;
      if (files.length > 0) {
        handleSelectedFile(files[0]);
      }
      event.target.value = "";
    },
    [handleSelectedFile],
  );

  const readFileAsText = (file, callback) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target) callback(file.name, e.target.result, file);
    };
    // For zip files, read as data URL; for JSON, read as text
    if (file.name.endsWith(".zip")) {
      reader.readAsDataURL(file);
    } else {
      reader.readAsText(file);
    }
  };

  return (
    <DropZone
      className={className}
      ref={dropZoneRef}
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
    >
      <Icon
        name="download"
        width="2rem"
        height="2rem"
      />
      <DropZoneText>
        {label ?? <Trans i18nKey="json_drop_label" />}
      </DropZoneText>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept={accept}
        onChange={handleFileChange}
      />
    </DropZone>
  );
};
